import React, { forwardRef } from 'react'
import { PseudoBox, PseudoBoxProps } from '@chakra-ui/core'

import { Box } from '../Box'
import { Spinner } from '../Spinner'
import { Icon, IconProps } from '../Icon'
import { useButtonStyle } from '../../hooks/useButtonStyle'

interface ButtonIconProps extends IconProps {
  icon: string | React.ElementType<any>
}

const ButtonIcon = ({ icon, ...props }: ButtonIconProps) => {
  if (typeof icon === 'string') {
    return (
      <Icon
        focusable={false}
        aria-hidden="true"
        name={icon}
        color="currentColor"
        fontSize="xl"
        {...props}
      />
    )
  }

  return (
    <Box
      as={icon}
      data-custom-icon
      focusable={false}
      aria-hidden="true"
      color="currentColor"
      {...props}
    />
  )
}

///////////////////////////////////////////////////////////////////////////////////////////////////

export interface ButtonProps extends PseudoBoxProps {
  isDisabled?: boolean
  isLoading?: boolean
  isActive?: boolean
  isFullWidth?: boolean
  children?: React.ReactNode
  variantColor?: string
  leftIcon?: string
  rightIcon?: string
  variant?: string
  loadingText?: string
  iconSpacing?: number
  type?: string
  size?: string
  hasDepth?: boolean
  form?: string
  isRound?: boolean
}

export const Button = forwardRef(
  (
    {
      isDisabled,
      isLoading,
      isActive,
      isFullWidth,
      children,
      as: Comp = 'button',
      variantColor = 'gray',
      leftIcon,
      rightIcon,
      variant = 'solid',
      hasDepth = true,
      loadingText,
      iconSpacing = 2,
      type = 'button',
      size = 'md',
      isRound = false,
      ...rest
    }: ButtonProps,
    ref,
  ) => {
    const _isDisabled: boolean = isDisabled || isLoading

    const buttonStyleProps = useButtonStyle({
      color: variantColor,
      variant,
      size,
      hasDepth,
      isDisabled: _isDisabled,
      isRound,
    })

    return (
      <PseudoBox
        ref={ref}
        disabled={_isDisabled}
        aria-disabled={_isDisabled}
        as={Comp}
        type={type}
        fontWeight="semibold"
        width={isFullWidth ? 'full' : undefined}
        data-active={isActive ? 'true' : undefined}
        {...buttonStyleProps}
        {...rest}
      >
        {leftIcon && !isLoading && <ButtonIcon ml={-1} mr={iconSpacing} icon={leftIcon} />}

        {isLoading && (
          <Spinner
            position={loadingText ? 'relative' : 'absolute'}
            mr={loadingText ? iconSpacing : 0}
            color="currentColor"
            size="sm"
          />
        )}

        {isLoading
          ? loadingText || (
              <Box as="span" opacity={0}>
                {children}
              </Box>
            )
          : children}

        {rightIcon && !isLoading && <ButtonIcon mr={-1} ml={iconSpacing} icon={rightIcon} />}
      </PseudoBox>
    )
  },
)

export default Button
